import { get } from "lodash";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { AUTH_PROVIDERS } from "utils/constants";
import { getAuthProvider } from "utils/helpers/authProvider";

/**
 * Provider-aware `signUpAuth`.
 *
 * - `KAYROS_ID`: runs the legacy `signUp` mutation routed straight to kayros-id
 *   via the `@privateAuthBff` directive to create the auth identity, then the
 *   caller chains the business mutation in `onCompleted` (two steps).
 * - `ZITADEL`: there is no separate auth step. The api provisions the IdP user
 *   inside the chained business mutation (signUp / requestSignUp / addUser), so
 *   we skip the network call entirely and immediately invoke `onCompleted` so
 *   the single business mutation runs.
 *
 * The result is always normalized under the `signUp` key so call sites can keep
 * destructuring `({ signUp: { id, username, email, meta } })` unchanged.
 */
export const useSignUpAuth = (hookOptions = {}) => {
  const isZitadel = getAuthProvider() === AUTH_PROVIDERS.ZITADEL;
  const [mutate, state] = useMutation(
    graphql.mutations.SIGN_UP_AUTH,
    hookOptions,
  );

  const signUpAuth = (options = {}) => {
    const { variables, onCompleted, ...rest } = options;

    if (isZitadel) {
      if (onCompleted) onCompleted({ signUp: get(variables, "data", {}) });
      return Promise.resolve();
    }

    return mutate({
      ...rest,
      variables,
      onCompleted: onCompleted
        ? (data) => onCompleted({ ...data, signUp: get(data, "signUp") })
        : undefined,
    });
  };

  return [signUpAuth, state];
};

export default useSignUpAuth;
